/**
 * Client for the FRED (St. Louis Fed) public Web API. Used for macroeconomic
 * time series Tiingo doesn't serve: WTI/Brent spot oil, Fed Funds Rate,
 * Treasury yields, etc.
 *
 * FRED's api.stlouisfed.org does not return CORS headers, so requests go
 * through a same-origin proxy at /fred/**. In dev that's handled by Vite's
 * server.proxy (see vite.config.ts); in production a Firebase Hosting rewrite
 * to a small proxy function is still required before this can ship.
 *
 * Requires a free API key registered at:
 *   https://fred.stlouisfed.org/docs/api/api_key.html
 * Set it in .env as VITE_FRED_API_KEY. FRED keys are per-user rate-limited
 * but not security-sensitive, so they're safe to ship in the bundle
 * (consistent with VITE_FIREBASE_API_KEY).
 *
 * FRED series are daily EOD (business days only) with a ~1-business-day
 * publication lag. Each observation is a single value; we map it to
 * open=high=low=close for compatibility with the OHLCV-shaped chart pipeline.
 */

import type { OHLCVPoint } from './types';

// Map user-facing tickers to FRED series IDs so the legend/tooltip can show a
// short label ("WTI") while the fetcher queries the canonical series.
// Browse the catalog at https://fred.stlouisfed.org/.
const FRED_SERIES_MAP: Record<string, string> = {
    WTI: 'DCOILWTICO',      // Crude Oil: WTI - Cushing, OK, $/bbl, daily
    BRENT: 'DCOILBRENTEU',  // Crude Oil: Brent - Europe, $/bbl, daily
    FFR: 'DFF',             // Effective Federal Funds Rate, %, daily
    TY10: 'DGS10',          // 10-Year Treasury Constant Maturity Rate, %, daily
};

interface FredObservation {
    date: string;       // "YYYY-MM-DD"
    value: string;      // numeric string, or "." for missing
}

interface FredResponse {
    observations?: FredObservation[];
    error_code?: number;
    error_message?: string;
}

function resolveSeriesId(ticker: string): string {
    const up = ticker.toUpperCase();
    return FRED_SERIES_MAP[up] ?? up;
}

function toIsoDate(d: Date): string {
    return d.toISOString().slice(0, 10);
}

// Module-level cache keyed by full request URL. Caches the parsed
// OHLCVPoint[] as a Promise so concurrent callers (e.g. React StrictMode
// double-invoke, rapid range switches) share a single in-flight request.
// FRED daily data updates once per business day with a publication lag, so
// session-scoped caching is always consistent. Failed requests are evicted
// so the next call retries fresh.
const fredCache = new Map<string, Promise<OHLCVPoint[]>>();

// FRED's Apache occasionally responds with `Upgrade: h2,h2c` which trips
// http-proxy (Vite's dev proxy) into returning 502 even though the upstream
// payload is fine - a ~5% transient failure rate in practice. One retry
// with a short backoff clears almost all of these without masking real
// upstream outages.
async function fetchWithRetry(url: string): Promise<Response> {
    let lastErr: unknown;
    for (let attempt = 0; attempt < 2; attempt++) {
        try {
            const response = await fetch(url);
            if (response.status < 500) return response;
            lastErr = new Error(`FRED proxy ${response.status}`);
        } catch (err) {
            lastErr = err;
        }
        if (attempt === 0) await new Promise(r => setTimeout(r, 200));
    }
    throw lastErr ?? new Error('FRED request failed');
}

export async function loadFredSeries(
    ticker: string,
    startDate: Date,
    endDate: Date
): Promise<OHLCVPoint[]> {
    const apiKey = import.meta.env.VITE_FRED_API_KEY as string | undefined;
    if (!apiKey) {
        throw new Error(
            'VITE_FRED_API_KEY not set. Register a free key at ' +
                'https://fred.stlouisfed.org/docs/api/api_key.html and add it to .env'
        );
    }

    const seriesId = resolveSeriesId(ticker);
    const params = new URLSearchParams({
        series_id: seriesId,
        api_key: apiKey,
        file_type: 'json',
        observation_start: toIsoDate(startDate),
        observation_end: toIsoDate(endDate),
        sort_order: 'asc',
    });

    const url = `/fred/series/observations?${params}`;

    const cached = fredCache.get(url);
    if (cached) return cached;

    const promise = (async () => {
        const response = await fetchWithRetry(url);

        if (!response.ok) {
            const body = await response.text().catch(() => '');
            throw new Error(
                `FRED API ${response.status} for ${seriesId}: ${body.slice(0, 200)}`
            );
        }

        const data = (await response.json()) as FredResponse;
        if (data.error_code) {
            throw new Error(
                `FRED API error ${data.error_code}: ${data.error_message ?? 'unknown'}`
            );
        }

        const obs = data.observations ?? [];
        const points: OHLCVPoint[] = [];
        for (const o of obs) {
            if (!o.value || o.value === '.') continue;
            const v = parseFloat(o.value);
            if (!Number.isFinite(v)) continue;
            points.push({
                t: new Date(o.date + 'T00:00:00Z'),
                open: v,
                high: v,
                low: v,
                close: v,
                vol: 0,
            });
        }
        return points;
    })().catch((err) => {
        fredCache.delete(url);
        throw err;
    });

    fredCache.set(url, promise);
    return promise;
}
